<script setup lang="ts">
import type { MyFormProvidedValue } from '../composables/useMyInput'

interface MyForm {
  refs: ReturnType<typeof useRefs>
  omit?: Readonly<(keyof this['refs'])[]>
  submit(): NotVoid
}

const t2 = useT2Base({
  Submit: 'Išsaugoti',
})

const props = defineProps<{
  enterSubmit?: boolean
  gap?: 'never'
  form?: MyForm
  txt?: stringI18n
  submit?(): NotVoid
  label?: 'never'
  sticky?: boolean
  classRewrite?: string | None
}>()

const timer = useTimeout()
const mobile = useMobile()

const data = shallowReactive({
  inputErrors: [] as { error: string; name: string; key: string }[],
  key: useId(),
  activated: false,
})

function reset() {
  data.activated = false
  data.inputErrors = []
  data.key = generateId()
}

function getRefs() {
  return props.form?.refs
}

// DEV ONLY CODE
const isDev = import.meta.dev
interface MyFormDevRef {
  isError(): boolean
  devValidateRefs(): void
}

const devError = useTemplateRef<MyFormDevRef>('elErrors')
const DEVerrors = computed<boolean>(() => devError.value?.isError() ?? false)

function doSubmit(submit: () => NotVoid): NotVoid {
  if (!data.activated) data.activated = true
  devError.value?.devValidateRefs()

  const obj = getRefs()
  if (obj) {
    const inputErrors: typeof data.inputErrors = []
    const omit = props.form?.omit

    for (const key in obj) {
      if (key === '__refs' || omit?.includes(key)) continue
      const res = getFormValueOneWithError(obj[key])

      if ('error' in res && res.error) {
        inputErrors.push({ key, error: res.error, name: getFormValueOneLabel(obj[key])?.label ?? key })
      }
    }
    if (inputErrors.length) data.inputErrors = inputErrors
  }

  const maybePromise = submit()
  if (helpers.isPromise(maybePromise)) {
    maybePromise.then((res) => getGqlResponse(res)?.success && timer.set(() => reset(), 60))
  }
  return maybePromise
}

const submitFunc = computed(() => {
  if (props.form?.submit) return () => doSubmit(() => props.form!.submit())
  if (props.submit) return () => doSubmit(() => props.submit!())
  return helpers.required(undefined, 'No submit function')
})

provide<MyFormProvidedValue>(myFormContext, {
  activated: () => data.activated,
  refs: () => getRefs(),
})

function scrollToRef(key: string): void {
  helpers.getHTMLElement(getRefs()?.[key]?.value)?.scrollIntoView({ behavior: 'smooth' })
  // scrollToElem(helpers.getHTMLElement(props.form?.refs?.[key]?.value), { offsetTop: 50 })
}
const elemSubmit = useTemplateRef('elemSubmit')
function submit123(e: Event) {
  if ('key' in e && e.key === 'Enter') {
    e.preventDefault()
    if (elemSubmit.value?.out.submit) elemSubmit.value?.out.submit?.()
    else submitFunc.value?.()
  }
}
function submitPrevet(e: Event) {
  e.preventDefault()
}
const events = computed(() => {
  if (!props.enterSubmit)
    return {
      onSubmit: (i: Event) => submitPrevet(i),
      // onKeydown: (i: Event) => submitPrevet(i),
    }
  return {
    onSubmit: (i: Event) => submit123(i),
    onKeydown: (i: Event) => submit123(i),
  }
})

const classMain = computed((): string => props.classRewrite ?? '_col _gap1')
</script>

<template>
  <form :id="data.key" :class="[classMain, data.activated && 'active', DEVerrors && '_bgRed _br9 _pt1 _pb1 _pl1 _pr1']" v-bind="events">
    <slot :key="data.key" />

    <ClientOnly v-if="isDev">
      <LazyMyFormDev ref="elErrors" :form="props.form" :submit="props.submit" />
    </ClientOnly>

    <div v-if="submitFunc" class="_row _pt1 _gap1 _wm70ch _hidden" :class="sticky && $style.sticky">
      <slot name="button" :submit="() => submitFunc?.() ?? null" :label="txt || t2('Submit')">
        <ButtonAction ref="elemSubmit" color="black" :txt="txt || t2('Submit')" :width="mobile ? 'grow' : '200'" :big="mobile" :submit="() => unvoidAny(submitFunc?.())" />
        <slot name="button2" />
      </slot>

      <LazyMyFormInfo v-if="data.inputErrors.length" :scroll-to-ref :input-errors="data.inputErrors" />
      <div v-else />
    </div>
  </form>
</template>

<style module>
.sticky {
  composes: global(_sticky);
  composes: global(_pb1);
  composes: global(_w100p);
  bottom: 0;
  background: hsla(0, 33.3%, 99%, 0.9);
}
</style>
